<template>
    <Dialog
        :value="showDialog"
        :header-text="headerText"
        :width="920"
        persistent
        hide-close-icon
        fixed
        @close_modal="$emit('close')"
    >
        <div>
            <v-row class="d-flex align-center">
                <v-col cols="4" class="pr-1">
                    <text-field
                        force-show-details
                        :value="signature.title"
                        :error-messages="getErrors('title')"
                        :label="$t('Signature name')"
                        :disabled="!canEditSignature"
                        @input="setFieldValue('title', $event)"
                    />
                </v-col>
                <v-col cols="2" class="pl-1 pr-1">
                    <dropdown
                        force-show-details
                        :items="layouts"
                        :selected="selectedLayout()"
                        :searchable="false"
                        :sort="false"
                        :label="$t('Layout')"
                        :disabled="!canEditSignature"
                        name-key="displayName"
                        value-key="value"
                        @input="setLayout($event)"
                    />
                </v-col>
                <v-col cols="2" class="pl-1 d-flex align-center py-0" style="min-height: 88px;">
                    <div class="flex-grow-1">
                        <div class="layout-block">
                            {{ $t('Top') }}
                        </div>
                        <div v-if="signature.layout === 'logo_left'" class="d-flex" style="margin-top: -2px; margin-bottom: -2px">
                            <div class="layout-block logo-block" style="margin-right: 1px !important;">
                                {{ $t('Logo') }}
                            </div>
                            <div class="layout-block" style="margin-left: 1px !important;">
                                {{ $t('Middle') }}
                            </div>
                        </div>
                        <div v-else-if="signature.layout === 'logo_top'">
                            <div class="layout-block logo-block">
                                {{ $t('Logo') }}
                            </div>
                            <div class="layout-block">
                                {{ $t('Middle') }}
                            </div>
                        </div>
                        <div v-else-if="signature.layout === 'no_logo'">
                            <div class="layout-block">
                                {{ $t('Middle') }}
                            </div>
                        </div>
                        <div class="layout-block">
                            {{ $t('Bottom') }}
                        </div>
                    </div>
                </v-col>
            </v-row>

            <v-row class="d-flex align-center">
                <v-col v-if="showIsForEveryone" cols="2" class="pt-0">
                    <SwitchButton
                        :disabled="!canEditSignature"
                        :value="signature.is_for_everyone"
                        :label="$t('Everyone')"
                        @change="signature.is_for_everyone = $event"
                    />
                </v-col>
                <v-col cols="3" class="pt-0">
                    <SwitchButton
                        :disabled="!canEditSignature"
                        :value="signature.is_default"
                        :label="$t('Default signature')"
                        @change="signature.is_default = $event"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" class="pt-0">
                    <RichTextEditor
                        ref="richTextEditor0"
                        :disabled="!canEditSignature"
                        :content="signature.content_top"
                        :toolbar="'#email-signature-toolbar-0'"
                        :label="$t('Top')"
                        @input="signature.content_top = $event"
                        @focus="setActiveRichTextEditor(0)"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="6" class="py-0">
                    <div class="d-flex flex-column">
                        <label
                            class="custom-label"
                            :class="getErrors('logo_url').length ? 'error-label-color' : 'default-label-color'"
                        >
                            {{ $t('Logo') }}
                        </label>
                        <v-row v-if="signature.logo_url">
                            <v-col cols="auto" class="py-0 pr-0">
                                <v-img
                                    contain
                                    height="60"
                                    max-width="180"
                                    min-width="60"
                                    :src="signature.logo_url"
                                >
                                </v-img>
                            </v-col>
                            <v-col cols="auto" class="pa-0">
                                <v-btn
                                    :disabled="!canEditSignature"
                                    class="text-lowercase-first-letter text-none"
                                    text
                                    small
                                    color="error"
                                    @click="removeLogo()"
                                >
                                    {{ $t('Delete logo') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                        <div v-else>
                            <FileUploadArea
                                :disabled="!canEditSignature"
                                :button-text="$t('Upload logo file')"
                                :file-upload="signatureLogoUpload"
                                :upload-change="uploadChange"
                                :upload-drop="uploadDrop"
                            >
                                <template #help>
                                    <div class="body-2 mt-2 font-weight-medium">
                                        {{ $t("Max. dimensions 130 x 130 px") }}
                                    </div>
                                </template>
                            </FileUploadArea>
                        </div>
                        <label
                            class="error-label-color custom-label"
                            style="font-weight: 400 !important; min-height: 20px"
                        >
                            {{ showErrors(getErrors('logo_url')) }}
                        </label>
                    </div>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" class="pt-0">
                    <RichTextEditor
                        ref="richTextEditor1"
                        :disabled="!canEditSignature"
                        :content="signature.content_middle"
                        :toolbar="'#email-signature-toolbar-1'"
                        :label="$t('Middle')"
                        @input="signature.content_middle = $event"
                        @focus="setActiveRichTextEditor(1)"
                    />
                </v-col>
            </v-row>

            <v-row class="">
                <v-col v-for="(link, index) in linkFields" :key="index" cols="4" class="py-0">
                    <text-field
                        force-show-details
                        :disabled="!canEditSignature"
                        :value="signature[link.field]"
                        :label="link.label"
                        :error-messages="getErrors(link.field)"
                        @input="setFieldValue(link.field, $event)"
                    />
                </v-col>
            </v-row>
            <v-row class="mb-6">
                <v-col cols="12" class="py-0">
                    <RichTextEditor
                        ref="richTextEditor2"
                        :disabled="!canEditSignature"
                        :content="signature.content_bottom"
                        :toolbar="'#email-signature-toolbar-2'"
                        :label="$t('Bottom')"
                        @input="signature.content_bottom = $event"
                        @focus="setActiveRichTextEditor(2)"
                    />
                </v-col>
            </v-row>

        </div>
        <template slot="footer">
            <SignatureFooter
                :activeRichTextEditorId="activeRichTextEditorId"
                :activeRichTextEditor="activeRichTextEditor"
                :disabled="!canEditSignature"
                @placeholder="addPlaceholder($event)"
                @deleteSignature="$emit('delete')"
                @save="$emit('save', signature)"
                @cancelModal="$emit('close')"
                @sendTestEmail="$emit('sendTestEmail', signature)"
            />
        </template>
    </Dialog>

</template>

<script>

import Dialog from "../../../components/Dialog.vue";
import {$t} from "../../../i18n";
import {ref, watch, computed} from "@vue/composition-api";
import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor.vue";
import FileUpload from "../../../composables/FileUpload";
import SignatureFooter from "./Footer.vue";
import FileUploadArea from "../../../components/CustomField/FileUploadArea.vue";
import Footer from "../EditEmailTemplateDialog/Footer.vue";
import appStoreDialog from "../../../store/modules/AppStoreDialog/appStoreDialog";
import important from "../../../components/RichDataTable/components/Important.vue";
import AccountModule from "../../../store/modules/account";
import appStoreDialogFlags from "../../../store/modules/AppStoreDialog/appStoreDialogFlags";

export default {
    name: "EditEmailSignatureDialog",
    computed: {
        important() {
            return important
        }
    },
    methods: {$t},
    components: {Footer, SignatureFooter, FileUploadArea, RichTextEditor, Dialog},
    props: {
        showDialog: {
            type: Boolean,
            default: null
        },
        signature: {
            type: Object,
            required: true
        },
        errors: {
            type: [Error, Object],
        },
    },
    setup(props, ctx) {
        const {
            appStoreDialogState,
            appStoreDialogGetters,
        } = appStoreDialog()
        const {canManageAccount} = appStoreDialogFlags()
        const {current_account} = AccountModule();
        const activeRichTextEditorId = ref(0)
        const activeRichTextEditor = ref(null)

        const richTextEditor0 = ref(null)
        const richTextEditor1 = ref(null)
        const richTextEditor2 = ref(null)
        const form_validation_errors = ref([])

        const layouts = ref([
            {
                displayName: $t("Logo left"),
                value: "logo_left"
            },
            {
                displayName: $t("Logo top"),
                value: "logo_top"
            },
            {
                displayName: $t("No logo"),
                value: "no_logo"
            },

        ])
        const linkFields = ref([
            {field: 'linkedin_link', label: $t('Linkedin')},
            {field: 'facebook_link', label: $t('Facebook')},
            {field: 'other_link', label: $t('Other')},
        ])

        const headerText = computed(() => {
            if (!canEditSignature.value) {
                return $t('Email signature')
            } else if (props.signature.id) {
                return $t('Edit email signature')
            } else {
                 return $t('Add email signature')
            }
        })

        const showIsForEveryone = computed(() => {
            return canManageAccount(current_account)
        })

        const canEditSignature = computed(() => {
            if (!props.signature.id) {
                // new signature
                return true
            }

            const currentAccountPersonId = current_account.value.id
            if (currentAccountPersonId !== props.signature.person_id) {
                return !!(canManageAccount(current_account) && props.signature.is_for_everyone);
            }
            // current account signature
            return true
        })

        const getLayoutImagePath = () => {
            switch (props.signature.layout) {
                case "logo_left":
                    return require('../../../assets/signature_layouts/logo_left.svg');
                case "logo_top":
                    return require('../../../assets/signature_layouts/logo_top.svg');
                case "no_logo":
                    return require('../../../assets/signature_layouts/no_logo.svg');
                default:
                    return null;
            }
        }

        const setLayout = (event) => {
            if (event.value === "no_logo") {
                // remove missing logo error
                form_validation_errors.value = form_validation_errors.value.filter(err => err.field !== 'logo_url')
                removeLogo()
            }
            props.signature.layout = event.value
        }

        const getEditorObject = (id) => {
            const editors = {
                0: richTextEditor0,
                1: richTextEditor1,
                2: richTextEditor2,
            }
            return editors[id]
        }

        const setActiveRichTextEditor = (id) => {
            activeRichTextEditorId.value = id
            activeRichTextEditor.value = getEditorObject(id)?.value
        }

        const addPlaceholder = (placeholder) => {
            let quill = activeRichTextEditor.value.$refs.richTextEditorContent.quill
            let selection = quill.getSelection()
            quill.insertText(selection.index, placeholder.value)
        }

        const setFieldValue = (field, value) => {
            props.signature[field] = value
            form_validation_errors.value = form_validation_errors.value.filter(err => err.field !== field)
        }

        const getErrors = (field) => {
            const paramErrors = []
            for (const error of form_validation_errors.value) {
                if (error.field === field) {
                    paramErrors.push(error.error_message)
                }
            }

            return paramErrors
        }

        const showErrors = (errors) => {
            return errors.join(", ")
        }

        watch(() => props.errors, () => {
            if (props.errors?.response?.data?.error_type === "form_validation") {
                form_validation_errors.value = props.errors.response.data.errors
            }
        })

        // Logo upload logic
        const relatedObject = ref({
            account_id: appStoreDialogGetters.getAccountIdFromAppData(),
            feature_id: appStoreDialogState.appData.id,
        })

        const uploadData = ref({
            files: []
        })

        const removeLogo = () => {
            props.signature.logo_url = null
        }
        const addLogo = (file) => {
            props.signature.logo_url = file.temp_name
        }

        const signatureLogoUpload = FileUpload("signature_logo", relatedObject, uploadData, false, addLogo, removeLogo, false);
        const selectedLayout = () => {
            return layouts.value.filter(layout => layout.value === props.signature.layout)
        }

        const uploadChange = async (e) => {
            signatureLogoUpload.upload_change(e)
            form_validation_errors.value = form_validation_errors.value.filter(err => err.field !== 'logo_url')
        }

        const uploadDrop = async function (e) {
            e.preventDefault();
            const files = [...e.dataTransfer.items].map(item => item.getAsFile());
            signatureLogoUpload.upload_drop({
                dataTransfer: {
                    files: files
                }
            })
        };

        return {
            layouts,
            selectedLayout,
            getLayoutImagePath,
            signatureLogoUpload,
            uploadChange,
            uploadDrop,
            linkFields,
            setActiveRichTextEditor,
            activeRichTextEditorId,
            activeRichTextEditor,
            richTextEditor0,
            richTextEditor1,
            richTextEditor2,
            addPlaceholder,
            removeLogo,
            setLayout,
            getErrors,
            setFieldValue,
            showErrors,
            showIsForEveryone,
            canEditSignature,
            headerText,
        }
    }
}

</script>

<style scoped lang="scss">
::v-deep .quill-editor {
    .ql-container {
        .ql-editor {
            min-height: 60px;
            height: unset !important;
        }
    }
}

.custom-label {
    font-size: 12px;
    line-height: 20px;
    font-weight: 600;
}

.error-label-color {
    color: rgb(244, 81, 30)
}

.default-label-color {
    color: var(--v-n3-base)
}

.layout-block {
    border-radius: 4px;
    margin: 2px;
    padding: 2px 6px;
    font-size: 9px;
    font-weight: 600;
    line-height: normal;
    background-color: var(--v-g2-base);
    display: flex;
    align-items: center;
    flex-grow: 1;
    text-transform: uppercase;
    color: var(--v-g4-base);

    &.logo-block {
        background-color: var(--v-primary-lighten2);
        padding-top: 11px;
        padding-bottom: 11px;
        flex-grow: 0;
    }
}

</style>